import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useLocationCore } from '../../../../core/modules/location';
import { useMasterCore } from '../../../../core/modules/master';
import { useWarehouseCore } from '../../../../core/modules/warehouse';
import { Location } from '../../../../core/interfaces/location.interface';

interface LocationSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (location: Location) => void;
  title: string;
  companyId?: number;
}

export const LocationSearchDialog: React.FC<LocationSearchDialogProps> = ({
  open,
  onClose,
  onSelect,
  title,
  companyId,
}) => {
  const { locations, getLocations } = useLocationCore();
  const { getTypes, locationTypes, channelTypes } = useMasterCore();
  const { getWarehouses, warehouses } = useWarehouseCore();
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocationType, setSelectedLocationType] = useState<string>('');
  const [selectedChannelType, setSelectedChannelType] = useState<string>('');
  const [searchType, setSearchType] = useState<'code' | 'warehouse_code'>('code');
  const [loading, setLoading] = useState(false);

  // Cargar datos necesarios al abrir el modal
  useEffect(() => {
    if (open && companyId) {
      setLoading(true);
      getLocations(companyId);
      getTypes(companyId, 'location');
      getTypes(companyId, 'channel');
      getWarehouses(companyId);
      setLoading(false);
    } else if (!open) {
      setSearchTerm('');
      setSelectedLocationType('');
      setSelectedChannelType('');
      setSearchType('code');
    }
  }, [open, companyId, getLocations, getTypes, getWarehouses]);

  const handleCopyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
      // Fallback para navegadores más antiguos
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    }
  };

  // Filtrar ubicaciones por término de búsqueda, tipo y canal
  const filteredLocations = useMemo(() => {
    if (!locations || locations.length === 0) return [];

    return locations.filter((location) => {
      // Filtrar por término de búsqueda según el tipo seleccionado
      let matchesSearch = true;
      if (searchTerm !== '') {
        if (searchType === 'code') {
          matchesSearch = location.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         location.description?.toLowerCase().includes(searchTerm.toLowerCase());
        } else if (searchType === 'warehouse_code') {
          // Buscar por código de bodega
          const warehouse = warehouses.find(w => w.id === location.warehouse_id);
          if (warehouse) {
            matchesSearch = warehouse.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           warehouse.description?.toLowerCase().includes(searchTerm.toLowerCase());
          } else {
            matchesSearch = false;
          }
        }
      }

      // Filtrar por tipo de ubicación
      const matchesLocationType = selectedLocationType === '' || 
        location.location_type_id.toString() === selectedLocationType;

      // Filtrar por tipo de canal
      const matchesChannelType = selectedChannelType === '' || 
        location.channel_type_ids?.some(channelId => channelId.toString() === selectedChannelType);

      return matchesSearch && matchesLocationType && matchesChannelType;
    });
  }, [locations, searchTerm, searchType, selectedLocationType, selectedChannelType, warehouses]);

  const handleLocationSelect = (location: Location) => {
    onSelect(location);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: 500,
          zIndex: 1300,
        },
      }}
      sx={{
        zIndex: 1300,
        '& .MuiBackdrop-root': {
          zIndex: 1299,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <LocationIcon color="primary" />
            <Typography variant="h6">{title}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Alerta de copia exitosa */}
          {copySuccess && (
            <Alert severity="success" onClose={() => setCopySuccess(null)}>
              {copySuccess}
            </Alert>
          )}

          {/* Filtros */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Filtros de búsqueda
            </Typography>
            
            {/* Switch para tipo de búsqueda */}
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography
                className={`text-sm cursor-pointer ${searchType === 'code' ? 'font-bold text-gray-700' : 'text-gray-500'}`}
                onClick={() => setSearchType('code')}
              >
                CODIGO UBICACION
              </Typography>
              <Typography
                className={`text-sm cursor-pointer ${searchType === 'warehouse_code' ? 'font-bold text-gray-700' : 'text-gray-500'}`}
                onClick={() => setSearchType('warehouse_code')}
              >
                CODIGO BODEGA
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              {/* Búsqueda por texto */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  placeholder={`Buscar por ${searchType === 'code' ? 'código de ubicación' : 'código de bodega'}`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Filtro por tipo de ubicación */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo Ubicación</InputLabel>
                  <Select
                    value={selectedLocationType}
                    onChange={(e) => setSelectedLocationType(e.target.value)}
                    label="Tipo Ubicación"
                  >
                    <MenuItem value="">Todos los tipos</MenuItem>
                    {locationTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id.toString()}>
                        {type.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Filtro por tipo de canal */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo Canal</InputLabel>
                  <Select
                    value={selectedChannelType}
                    onChange={(e) => setSelectedChannelType(e.target.value)}
                    label="Tipo Canal"
                  >
                    <MenuItem value="">Todos los canales</MenuItem>
                    {channelTypes.map((channel) => (
                      <MenuItem key={channel.id} value={channel.id.toString()}>
                        {channel.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Lista de ubicaciones */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Ubicaciones disponibles ({filteredLocations.length})
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : filteredLocations.length === 0 ? (
              <Alert severity="info">
                No se encontraron ubicaciones con los filtros aplicados.
              </Alert>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {filteredLocations.map((location, index) => {
                  const warehouse = warehouses.find(w => w.id === location.warehouse_id);
                  return (
                    <React.Fragment key={location.id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => handleLocationSelect(location)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar>
                              <LocationIcon />
                            </Avatar>
                          </ListItemAvatar>
                          
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1" fontWeight="medium">
                                  {location.code || 'Sin código'}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (location.code) {
                                      handleCopyToClipboard(location.code);
                                    }
                                  }}
                                  sx={{ 
                                    color: 'text.secondary', 
                                    '&:hover': { 
                                      color: 'primary.main', 
                                      backgroundColor: 'action.hover' 
                                    } 
                                  }}
                                  title="Copiar código al portapapeles"
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {location.description || 'Sin descripción'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Tipo: {location.location_type_id} | Canales: {location.channel_type_ids?.join(', ') || 'N/A'} | Bodega: {warehouse?.code || 'N/A'}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                      
                      {index < filteredLocations.length - 1 && (
                        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mx: 2 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationSearchDialog;
